import { useCallback, useMemo } from 'react'
import Supercluster from 'supercluster'

import type { LngLat, MapHandle } from 'lib/maps/types'

const CLUSTER_RADIUS = 60
const CLUSTER_MAX_ZOOM = 16
const CLUSTER_MIN_POINTS = 2
const MAX_EXPANSION_ZOOM = 20
const DEFAULT_EXPANSION_DURATION_MS = 500
const WORLD_BOUNDS: [number, number, number, number] = [-180, -85, 180, 85]

type MarkerPointProperties<T extends LngLat> = T & { originalIndex: number; cluster?: undefined }

export type MapPointFeature<T extends LngLat> = Supercluster.PointFeature<MarkerPointProperties<T>>
export type MapClusterFeature = Supercluster.ClusterFeature<Record<string, never>>

export function isMapClusterFeature<T extends LngLat>(
  feature: MapClusterFeature | MapPointFeature<T>,
): feature is MapClusterFeature {
  return feature.properties.cluster === true
}

interface UseMapboxClustersArgs<T extends LngLat> {
  markers: Array<T>
  mapHandle?: MapHandle | null
  zoom: number
  disableClustering?: boolean
  expansionDurationMs?: number
}

export function useMapboxClusters<T extends LngLat>({
  markers,
  mapHandle,
  zoom,
  disableClustering = false,
  expansionDurationMs = DEFAULT_EXPANSION_DURATION_MS,
}: UseMapboxClustersArgs<T>) {
  const geojsonPoints: Array<MapPointFeature<T>> = useMemo(() => markers.map((marker, index) => ({
    type: 'Feature' as const,
    properties: { ...marker, originalIndex: index },
    geometry: {
      type: 'Point' as const,
      coordinates: [marker.lng, marker.lat] as [number, number],
    },
  })), [markers])

  const clusterIndex = useMemo(() => {
    if (disableClustering) {
      return null
    }

    const nextClusterIndex = new Supercluster<MarkerPointProperties<T>, Record<string, never>>({
      radius: CLUSTER_RADIUS,
      maxZoom: CLUSTER_MAX_ZOOM,
      minPoints: CLUSTER_MIN_POINTS,
    })

    nextClusterIndex.load(geojsonPoints)
    return nextClusterIndex
  }, [disableClustering, geojsonPoints])

  const clusters = useMemo(() => {
    if (!clusterIndex) {
      return geojsonPoints
    }

    return clusterIndex.getClusters(WORLD_BOUNDS, Math.floor(zoom))
  }, [clusterIndex, geojsonPoints, zoom])

  const handleClusterClick = useCallback((clusterId: number, coordinate: LngLat) => {
    if (!clusterIndex || !mapHandle) {
      return
    }

    const expansionZoom = Math.min(clusterIndex.getClusterExpansionZoom(clusterId), MAX_EXPANSION_ZOOM)

    mapHandle.setViewport({
      latitude: coordinate.lat,
      longitude: coordinate.lng,
      zoom: expansionZoom,
    }, {
      animate: true,
      durationMs: expansionDurationMs,
      essential: true,
    })
  }, [clusterIndex, expansionDurationMs, mapHandle])

  return {
    clusters,
    handleClusterClick,
  }
}
